import { Box } from '@mui/material';
import { useOnboardingStore } from 'store/useOnboardingStore';
import { useToast } from 'components/Providers/ToastProvider';
import { isValidWebsiteUrl } from 'util/isValidWebsiteUrl';
import { useState } from 'react';
import { CircularProgress } from '@mui/material';
import { HelloMuiLoadingButton } from 'components';

import { CustomOutlinedInput } from '../../auth';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { FormControl, MenuItem, Select, InputAdornment } from '@mui/material';
import useUserStore from 'store/useUserStore';

const IntroduceSection = () => {
    const { account, setAccount, saveOnboardingInfo } = useOnboardingStore();
    const { showMessage } = useToast();
    const { userInfo } = useUserStore();
    const [isLoading, setIsLoading] = useState(false);

    const referralSources = ['Google Search', 'Social Media', 'Friend/Colleague', 'Advertisement', 'Email Marketing', 'Other'];

    const handleSaveOnboardingInfo = async () => {
        if (!account.type) {
            showMessage('Alert', 'Please select an account type!');
            return;
        }
        if (!account.referralSource) {
            showMessage('Alert', 'Please select where did you hear about us!');
            return;
        }
        if (!isValidWebsiteUrl(account.websiteUrl)) {
            showMessage('Alert', 'Please enter a valid website URL!');
            return;
        }
        setIsLoading(true);
        try {
            await saveOnboardingInfo();
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field: keyof typeof account, value: string) => {
        setAccount({
            ...account,
            [field]: value,
        });
    };

    return (
        <Box
            className="lg:h-full lg:flex"
            sx={{
                height: 'calc(100vh - 135px)',
            }}
        >
            <div className="lg:w-2/5 lg:flex items-center lg:pt-0 px-[20px] py-[42px]">
                <div className="text-center lg:text-left flex flex-col gap-[16px] lg:translate-y-[-50%]">
                    <div className="font-semibold">TELL US ABOUT YOURSELF</div>
                    <div className="font-semibold text-[24px] text-my-blue leading-[21px]">{"We're thrilled to have you join us!"}</div>
                    <div className="leading-[24px]">To provide you with the best possible experience, we require some relevant information.</div>
                </div>
            </div>

            <div className="lg:w-3/5 lg:p-[20px] pb-[60px]">
                <div className="lg:border border-my-gray lg:rounded-[10px] p-[20px] flex flex-col gap-[16px] h-full bg-white bg-opacity-20 overflow-auto">
                    <div className="font-semibold text-[18px]">Account Setup</div>

                    {/* Account Type Selection */}
                    <div>
                        <div className="text-[16px] font-medium mb-[8px]">Account Type</div>
                        <div className="text-[14px] text-gray-600 mb-[12px]">Are you creating this account on behalf of a company or for yourself?</div>
                        <div className="flex h-[42px] min-h-[42px] overflow-hidden w-fit">
                            <div
                                className={`flex cursor-pointer bg-my-gray bg-opacity-20 px-[20px] items-center gap-[10px] border rounded-l-[10px] ${account.type === 'Personal' ? 'bg-my-green bg-opacity-20 border-my-green' : 'border-my-gray '}`}
                                onClick={() => {
                                    setAccount({
                                        ...account,
                                        type: 'Personal',
                                    });
                                }}
                                data-cy="personal-account"
                            >
                                <img alt={'Personal '} src="/image/OnBoarding/PersonalAccount.png" className="w-[24px] h-[24px]" />
                                <div>Personal</div>
                            </div>

                            <div
                                className={`flex cursor-pointer bg-my-gray bg-opacity-20 px-[20px] items-center gap-[10px] border rounded-r-[10px] ${account.type === 'Company' ? 'bg-my-green bg-opacity-20 border-my-green' : 'border-my-gray'}`}
                                onClick={() =>
                                    setAccount({
                                        ...account,
                                        type: 'Company',
                                    })
                                }
                                data-cy="company-account"
                            >
                                <img alt={'Company '} src="/image/OnBoarding/CompanyAccount.png" className="w-[24px] h-[24px]" />
                                <div>{'Company'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Website URL Field */}
                    <div>
                        <label className="block text-[16px] font-medium mb-[8px]">Website URL</label>
                        <CustomOutlinedInput
                            name="websiteUrl"
                            startIcon={<LanguageOutlinedIcon />}
                            placeholder="Enter your website URL"
                            value={account.websiteUrl}
                            onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                            className="w-full"
                        />
                    </div>

                    {/* Referral Source Field */}
                    <div>
                        <label className="block text-[16px] font-medium mb-[8px]">Where did you hear about us?</label>
                        <FormControl fullWidth>
                            <Select
                                value={account.referralSource}
                                onChange={(e) => handleInputChange('referralSource', e.target.value)}
                                displayEmpty
                                className="!rounded-[8px] h-[50px] bg-white bg-opacity-40"
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <span className="text-gray-500">Select an option</span>;
                                    }
                                    return selected;
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px !important',
                                        height: '50px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#bdbdbd',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3176bc',
                                        },
                                    },
                                    '& .MuiSelect-select': {
                                        padding: '0px 14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                {referralSources.map((source) => (
                                    <MenuItem key={source} value={source}>
                                        {source}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>
            </div>
            <div className="fixed bottom-0 left-0 right-0 bg-white bg-opacity-20 backdrop-blur-sm border-t border-my-gray p-4 z-10">
                <div className="max-w-[1200px] mx-auto flex justify-end">
                    <HelloMuiLoadingButton
                        className="!h-[42px] !rounded-[5px] !min-w-[120px]"
                        onClick={handleSaveOnboardingInfo}
                        data-cy="finish-button"
                        loading={isLoading}
                        variant="contained"
                        loadingIndicator={<CircularProgress color="inherit" size={24} />}
                        sx={{
                            backgroundColor: '#3176bc',
                            '&:hover': {
                                backgroundColor: '#2563a8',
                            },
                        }}
                    >
                        {isLoading ? '' : 'Finish'}
                    </HelloMuiLoadingButton>
                </div>
            </div>
        </Box>
    );
};

export default IntroduceSection;
